import React, { useRef, useEffect } from 'react';
import cardFrontImg from '../../../images/card-front.png';
import cvsCardFrontImg from '../../../images/cvs-front.png';

/**
 * CanvasCardFront
 * Renders a card-front image on an HTML5 canvas and draws dynamic
 * text (Member ID, BIN, GROUP, PCN) on top at high-DPI resolution.
 *
 * Props:
 *   isCvs       {boolean}  – use the CVS front image
 *   memberId    {string}
 *   bin         {string}
 *   pcn         {string}
 *   groupNumber {string}
 *   className   {string}   – extra CSS classes for the <canvas> wrapper
 */
function CanvasCardFront({ isCvs, memberId = '', bin = '', pcn = '', groupNumber = '', className = '' }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        drawFrontCard();
    }, [isCvs, memberId, bin, pcn, groupNumber]);

    const drawFrontCard = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const src = isCvs ? cvsCardFrontImg : cardFrontImg;
        const img = new Image();
        img.onload = () => {
            const scale = Math.max(window.devicePixelRatio || 1, 2);
            const W = img.width;
            const H = img.height;

            canvas.width  = W * scale;
            canvas.height = H * scale;
            canvas.style.width  = '100%';
            canvas.style.height = 'auto';

            const ctx = canvas.getContext('2d');
            ctx.scale(scale, scale);

            // Draw the card image
            ctx.drawImage(img, 0, 0, W, H);

            // Cover any placeholder text with transparent rect
            ctx.fillStyle = 'rgba(255,255,255,0.0)';
            ctx.fillRect(0, H * 0.3, W, H * 0.35);

            // Text styling
            const fontSize = W * 0.044;
            ctx.font        = `bold ${fontSize}px Arial, sans-serif`;
            ctx.fillStyle   = '#FFFFFF';
            ctx.shadowColor = 'rgba(0,0,0,0.4)';
            ctx.shadowBlur  = 3;

            // Vertical start — CVS card text sits a bit lower
            const startY = isCvs ? H * 0.46 : H * 0.38;
            const lineH  = fontSize * 1.4;

            // Horizontal bounds match the "Show this card" strip width in the image
            const stripLeft  = W * 0.12;
            const stripRight = W * 0.94;
            const midX       = stripLeft + (stripRight - stripLeft) * 0.5;

            ctx.textAlign = 'left';

            // ── Left half: Member ID label + value ────────────────────────
            ctx.font = `bold ${fontSize}px Arial, sans-serif`;
            ctx.fillText('Member ID', stripLeft, startY);
            ctx.font = `${fontSize}px Arial, sans-serif`;
            ctx.fillText(memberId || '', stripLeft, startY + lineH);

            // ── Right half: BIN / PCN / GROUP ─────────────────────────────
            const labelPad = W * 0.015;
            ctx.font = `bold ${fontSize}px Arial, sans-serif`;

            const binW   = ctx.measureText('Bin:').width;
            const pcnW   = ctx.measureText('PCN:').width;
            const groupW = ctx.measureText('Group:').width;

            ctx.fillText('Bin:',   midX, startY);
            ctx.fillText('PCN:',   midX, startY + lineH);
            ctx.fillText('Group:', midX, startY + lineH * 2);

            ctx.font = `${fontSize}px Arial, sans-serif`;
            ctx.fillText(bin         || '', midX + binW   + labelPad, startY);
            ctx.fillText(pcn         || '', midX + pcnW   + labelPad, startY + lineH);
            ctx.fillText(groupNumber || '', midX + groupW + labelPad, startY + lineH * 2);

            ctx.shadowBlur = 0;
        };
        img.src = src;
    };

    return (
        <canvas
            ref={canvasRef}
            style={{ borderRadius: 16, display: 'block', height: 'auto' }}
            className={`w-full ${className}`}
            aria-label="Prescription Savings Card"
        />
    );
}

export default CanvasCardFront;
